import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Default column configurations for different views
const DEFAULT_COLUMNS = {
  quotation: [
    'name',
    'product_id',
    'specification',
    'quantity',
    'unit_price',
    'total_price',
    'unit_price_tax_included',
    'total_price_tax_included'
  ],
  sales: [
    'name',
    'product_id',
    'client_ref_number',
    'specification',
    'quantity',
    'price',
    'total_price',
    'unit_price_tax_included',
    'total_price_tax_included'
  ],
  purchase: [
    'name', 
    'product_id', 
    'specification', 
    'quantity', 
    'price', 
    'total_price', 
    'unit_price_tax_included', 
    'total_price_tax_included'
  ],
  inventory: ['name', 'product_id', 'category', 'stock_status', 'price']
};

// Calculated columns that are automatically computed
const CALCULATED_COLUMNS = {
  // Total price (exclusive of tax)
  total_price: (product) => {
    const quantity = product.quantity || 0;
    const baseUnit = product.unit_price ?? product.price ?? 0;
    const total = quantity * baseUnit;
    return Math.round(total * 100) / 100;
  },
  // Unit price (inclusive of tax) - NO rounding on unit prices
  unit_price_tax_included: (product, taxRate = 0.13) => {
    // If it's already on the product, trust it, as it's the source of truth from user input.
    if (typeof product.unit_price_tax_included === 'number') {
      return product.unit_price_tax_included;
    }
    // Otherwise, calculate it from the exclusive price (no rounding on unit price).
    const unitExclusive = product.unit_price ?? product.price ?? 0;
    return unitExclusive * (1 + taxRate);
  },
  // Total price (inclusive of tax) - unit price NOT rounded, only total is rounded
  total_price_tax_included: (product, taxRate = 0.13) => {
    const quantity = product.quantity || 0;

    // Determine the single-unit tax-inclusive price (no rounding on unit price)
    const unitInclusive = (typeof product.unit_price_tax_included === 'number')
      ? product.unit_price_tax_included
      : (product.unit_price ?? product.price ?? 0) * (1 + taxRate);

    // Only round the final total, not the unit price
    const total = quantity * unitInclusive;
    return Math.round(total * 100) / 100;
  }
};

// Check if a column is calculated/special
const isCalculatedColumn = (columnId) => {
  return Object.keys(CALCULATED_COLUMNS).includes(columnId);
};

// Storage keys
const STORAGE_KEYS = {
  quotation: 'column_preferences_quotation',
  sales: 'column_preferences_sales', 
  purchase: 'column_preferences_purchase',
  inventory: 'column_preferences_inventory'
};

const ColumnPreferencesContext = createContext();

export const useColumnPreferences = () => {
  const context = useContext(ColumnPreferencesContext);
  if (!context) {
    throw new Error('useColumnPreferences must be used within a ColumnPreferencesProvider');
  }
  return context;
};

export const ColumnPreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const loadPreferences = () => {
      const loadedPreferences = {};
      
      Object.keys(DEFAULT_COLUMNS).forEach(view => {
        try {
          const stored = localStorage.getItem(STORAGE_KEYS[view]);
          loadedPreferences[view] = stored 
            ? JSON.parse(stored) 
            : DEFAULT_COLUMNS[view];
        } catch (error) {
          console.warn(`Failed to load column preferences for ${view}:`, error);
          loadedPreferences[view] = DEFAULT_COLUMNS[view];
        }
      });
      
      setPreferences(loadedPreferences);
      setLoading(false);
    };

    loadPreferences();
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((view, columns) => {
    try {
      localStorage.setItem(STORAGE_KEYS[view], JSON.stringify(columns));
      setPreferences(prev => ({
        ...prev,
        [view]: columns
      }));
    } catch (error) {
      console.error(`Failed to save column preferences for ${view}:`, error);
    }
  }, []);

  // Get columns for a specific view
  const getColumns = useCallback((view) => {
    return preferences[view] || DEFAULT_COLUMNS[view] || [];
  }, [preferences]);

  // Update columns for a specific view
  const updateColumns = useCallback((view, columns) => {
    savePreferences(view, columns);
  }, [savePreferences]);

  // Reset columns to default for a specific view
  const resetColumns = useCallback((view) => {
    const defaultColumns = DEFAULT_COLUMNS[view] || [];
    savePreferences(view, defaultColumns);
  }, [savePreferences]);

  // Reset all columns to defaults
  const resetAllColumns = useCallback(() => {
    Object.keys(DEFAULT_COLUMNS).forEach(view => {
      savePreferences(view, DEFAULT_COLUMNS[view]);
    });
  }, [savePreferences]);

  // Get column configuration with metadata
  const getColumnConfig = useCallback((view) => {
    const columns = getColumns(view);
    return {
      columns,
      count: columns.length,
      view,
      isDefault: JSON.stringify(columns) === JSON.stringify(DEFAULT_COLUMNS[view])
    };
  }, [getColumns]);

  // Check if columns are modified from default
  const hasCustomColumns = useCallback((view) => {
    const currentColumns = getColumns(view);
    const defaultColumns = DEFAULT_COLUMNS[view] || [];
    return JSON.stringify(currentColumns) !== JSON.stringify(defaultColumns);
  }, [getColumns]);

  // Export current preferences (for backup/sharing)
  const exportPreferences = useCallback(() => {
    return {
      version: '1.0',
      timestamp: new Date().toISOString(),
      preferences: preferences
    };
  }, [preferences]);

  // Import preferences (from backup/sharing)
  const importPreferences = useCallback((importedData) => {
    try {
      if (importedData.version === '1.0' && importedData.preferences) {
        Object.entries(importedData.preferences).forEach(([view, columns]) => {
          if (DEFAULT_COLUMNS[view] && Array.isArray(columns)) {
            savePreferences(view, columns);
          }
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to import preferences:', error);
      return false;
    }
  }, [savePreferences]);

  // Calculate value for a calculated column
  const calculateColumnValue = useCallback((columnId, product, options = {}) => {
    if (!isCalculatedColumn(columnId)) {
      return product[columnId];
    }

    const calculator = CALCULATED_COLUMNS[columnId];
    const taxRate = options.taxRate || 0.13; // Default 13% tax rate

    try {
      const value = calculator(product, taxRate);
      // Only round total columns, not unit price columns
      if (typeof value === 'number' && (columnId === 'total_price' || columnId === 'total_price_tax_included')) {
        return Number(value.toFixed(2));
      }
      return value;
    } catch (error) {
      console.warn(`Error calculating ${columnId}:`, error);
      return 0;
    }
  }, []);

  // Get enhanced product data with calculated columns
  const getEnhancedProduct = useCallback((product, options = {}) => {
    const enhanced = { ...product };
    
    // Calculate all calculated columns
    Object.keys(CALCULATED_COLUMNS).forEach(columnId => {
      enhanced[columnId] = calculateColumnValue(columnId, product, options);
    });
    
    return enhanced;
  }, [calculateColumnValue]);

  // Batch enhance multiple products
  const getEnhancedProducts = useCallback((products, options = {}) => {
    return products.map(product => getEnhancedProduct(product, options));
  }, [getEnhancedProduct]);

  // Get available views
  const getViews = useCallback(() => {
    return Object.keys(DEFAULT_COLUMNS);
  }, []);

  // Check if any calculated columns are in current view
  const hasCalculatedColumns = useCallback((view) => {
    const columns = getColumns(view);
    return columns.some(columnId => isCalculatedColumn(columnId));
  }, [getColumns]);

  const contextValue = {
    // State
    preferences,
    loading,
    
    // Column operations
    getColumns,
    updateColumns,
    resetColumns,
    resetAllColumns,
    getColumnConfig,
    
    // Calculated column utilities
    calculateColumnValue,
    getEnhancedProduct,
    getEnhancedProducts,
    hasCalculatedColumns,
    isCalculatedColumn,
    
    // Utilities
    hasCustomColumns,
    exportPreferences,
    importPreferences,
    getViews,
    
    // Constants
    defaultColumns: DEFAULT_COLUMNS,
    calculatedColumns: CALCULATED_COLUMNS
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <ColumnPreferencesContext.Provider value={contextValue}>
      {children}
    </ColumnPreferencesContext.Provider>
  );
};

export default ColumnPreferencesContext;
